/**
 * Service for location-related functionality including distance calculation
 * and map bounds management
 */
export class LocationService {
  private static instance: LocationService;
  private cachedDistances: Map<string, number> = new Map();
  
  private constructor() {}
  
  /**
   * Get the singleton instance of LocationService
   */
  public static getInstance(): LocationService {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }
  
  /**
   * Calculate distance between two points using the Haversine formula
   * @param lat1 Latitude of first point
   * @param lng1 Longitude of first point
   * @param lat2 Latitude of second point
   * @param lng2 Longitude of second point
   * @returns Distance in meters
   */
  public calculateDistance(
    lat1: number, 
    lng1: number, 
    lat2: number, 
    lng2: number
  ): number {
    // If either point is invalid, return -1
    if (!this.isValidCoordinate(lat1, lng1) || !this.isValidCoordinate(lat2, lng2)) {
      return -1;
    }
    
    // Convert to radians
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371000; // Earth radius in meters
    
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return Math.round(distance); // Round to nearest meter
  }
  
  /**
   * Calculate the distance between a user and a course
   * @param userLat User's latitude
   * @param userLng User's longitude
   * @param courseId Course ID (for caching)
   * @param courseLat Course latitude
   * @param courseLng Course longitude
   * @returns Distance in meters
   */
  public calculateDistanceToCourse(
    userLat: number, 
    userLng: number, 
    courseId: string,
    courseLat: number, 
    courseLng: number
  ): number {
    // Create a cache key
    const cacheKey = `${userLat},${userLng}-${courseId}`;
    
    // Check if this distance is already cached
    if (this.cachedDistances.has(cacheKey)) {
      return this.cachedDistances.get(cacheKey)!;
    }
    
    // Calculate the distance
    const distance = this.calculateDistance(userLat, userLng, courseLat, courseLng);
    
    // Cache the result
    this.cachedDistances.set(cacheKey, distance);
    
    return distance;
  }
  
  /**
   * Format distance in meters or kilometers
   * @param distance Distance in meters
   * @returns Formatted distance string
   */
  public formatDistance(distance: number): string {
    if (distance < 0) return '';
    
    if (distance < 1000) {
      return `${distance} m`;
    } else {
      return `${(distance / 1000).toFixed(1)} km`;
    }
  }
  
  /**
   * Check if a coordinate is valid
   * @param lat Latitude
   * @param lng Longitude
   * @returns True if the coordinate is valid
   */
  private isValidCoordinate(lat: number, lng: number): boolean {
    return (
      !isNaN(lat) && 
      !isNaN(lng) && 
      lat >= -90 && 
      lat <= 90 && 
      lng >= -180 && 
      lng <= 180
    );
  }
  
  /**
   * Create an expanded bounds object with a buffer
   * @param bounds Google Maps bounds object
   * @param bufferPercentage Percentage to expand bounds by (default: 5%)
   * @returns Expanded bounds object
   */
  public createExpandedBounds(
    bounds: google.maps.LatLngBounds,
    bufferPercentage: number = 5
  ): {
    north: number;
    south: number;
    east: number;
    west: number;
  } {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    const latDiff = ne.lat() - sw.lat();
    const lngDiff = ne.lng() - sw.lng();
    
    const latBuffer = latDiff * (bufferPercentage / 100);
    const lngBuffer = lngDiff * (bufferPercentage / 100);
    
    return {
      north: ne.lat() + latBuffer,
      south: sw.lat() - latBuffer,
      east: ne.lng() + lngBuffer,
      west: sw.lng() - lngBuffer
    };
  }
  
  /**
   * Check if a course is within expanded bounds
   * @param course Course to check
   * @param expandedBounds Expanded bounds object
   * @returns True if the course is within the expanded bounds
   */
  public isCourseInBounds(
    course: any,
    expandedBounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    }
  ): boolean {
    // Get course coordinates
    let lat, lng;
    
    if (course.coordinates && typeof course.coordinates.lat === 'number' && typeof course.coordinates.lng === 'number') {
      lat = course.coordinates.lat;
      lng = course.coordinates.lng;
    } else if (typeof course.X === 'number' && typeof course.Y === 'number') {
      lat = course.Y; // Note: Metrix uses X for lng and Y for lat
      lng = course.X;
    } else if (typeof course.X === 'string' && typeof course.Y === 'string') {
      lat = parseFloat(course.Y);
      lng = parseFloat(course.X);
    } else {
      return false; // Invalid coordinates
    }
    
    // Check if coordinates are within bounds
    return (
      lat >= expandedBounds.south &&
      lat <= expandedBounds.north &&
      lng >= expandedBounds.west &&
      lng <= expandedBounds.east
    );
  }
  
  /**
   * Clear the distance cache
   */
  public clearDistanceCache(): void {
    this.cachedDistances.clear();
  }
}
